
import React from "react";
import { ArrowRight, Clock, CheckCircle, ShieldCheck, Gauge } from "lucide-react";
import { Link } from "react-router-dom";
import Image from "@/components/ui/image";
import { LainaCta } from "@/components/common/LainaCta";
import { useIsMobile } from "@/hooks/use-mobile";
import { CombineLoanCalculator } from "./CombineLoanCalculator";

export const YhdistelylainaHero = () => {
  const isMobile = useIsMobile();

  return (
    <section className="relative h-auto lg:min-h-[650px] flex flex-col justify-between mb-6 lg:mb-12">
      {/* Background image with overlay for better text visibility */}
      <div className="absolute inset-0 z-0">
        <Image
          src="/lovable-uploads/09d16f10-15b2-4216-82a9-4cc58b517a83.png"
          alt="Yhdistelylaina auttaa vähentämään taloudellista stressiä"
          className="w-full h-full object-cover"
          priority={true}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/60 to-black/55"></div>
      </div>

      {/* Hero content */}
      <div className="relative z-10 w-full py-8 sm:py-12 lg:py-16">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left content (headings, CTA, benefits) */}
            <div className="lg:col-span-6 xl:col-span-5">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight text-white mb-4 drop-shadow-md">
                Yhdistä lainasi <span className="text-blue-400">edullisemmin</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/90 mt-4 mb-6 drop-shadow-md">
                Säästä jopa tuhansia euroja yhdistämällä kaikki lainasi yhdeksi edullisemmaksi kokonaisuudeksi.
              </p>
              
              {/* Mobile: Simple benefits (only on mobile) */}
              {isMobile ? (
                <div className="mt-6 mb-8">
                  <div className="flex flex-wrap gap-2 mb-6">
                    <div className="bg-blue-500/80 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm font-medium">
                      ✓ Alhaisempi korko
                    </div>
                    <div className="bg-green-500/80 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm font-medium">
                      ✓ Yksi kuukausierä
                    </div>
                    <div className="bg-purple-500/80 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm font-medium">
                      ✓ Selkeä hallinta
                    </div>
                  </div>
                </div>
              ) : (
                /* Desktop: Full benefits grid */
                <div className="mt-6 mb-8">
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-3 drop-shadow-md">Miksi valita yhdistelylaina?</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 text-white">
                      <span className="font-bold">Selkeytä talouttasi:</span>{" "}
                      Vaihda useat lainat yhdeksi ja helpota arkea.
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 text-white">
                      <span className="font-bold">Säästä korkokuluissa:</span>{" "}
                      Edullisempi yhdistelylaina tuo merkittäviä säästöjä.
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 text-white">
                      <span className="font-bold">Pienennä kuukausierää:</span>{" "}
                      Yksi laina, mahdollisuus pienempään kuukausierään.
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 text-white">
                      <span className="font-bold">Paranna luottoluokitustasi:</span>{" "}
                      Useiden lainojen yhdistäminen yhdeksi voi auttaa parantamaan luottoasi.
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <LainaCta 
                  text="Hae yhdistelylainaa"
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold"
                  variant="primary"
                  size="lg"
                />
                <Link
                  to="/lainatuotteet"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 border border-white/30 backdrop-blur-sm text-white font-medium transition-all duration-300"
                >
                  Vertaile lainoja <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
            
            {/* Right side: calculator (desktop only) - adjusted position */}
            <div className="hidden lg:block lg:col-span-6 xl:col-span-7 h-full">
              <div className="flex justify-center xl:justify-end items-center h-full">
                <div className="w-full max-w-md">
                  <CombineLoanCalculator />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Benefits row at the bottom of hero - only on desktop */}
      <div className="hidden lg:block relative z-20 container mx-auto px-4 md:px-8 pb-6 md:pb-8">
        <div className="flex flex-wrap justify-center md:justify-start gap-2 md:gap-4">
          {[
            { text: "Päätös 24h sisällä", icon: <Clock className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: "Edulliset korot alkaen 5,9%", icon: <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: "Ei vaadi vakuuksia", icon: <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: "Maksuaika 1-15 vuotta", icon: <Gauge className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> }
          ].map((item, i) => (
            <div key={i} className="bg-white px-3 py-2 md:px-6 md:py-3 rounded-full shadow-md flex items-center gap-1.5 md:gap-2 text-sm md:text-base">
              {item.icon}
              <span className="font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Mobile calculator - simplified and prominent */}
      <div className="lg:hidden relative z-10 container mx-auto px-4 pb-8">
        <div className="w-full">
          <CombineLoanCalculator />
        </div>
      </div>
    </section>
  );
};

